import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import type { BuildupGrade } from '@/types/coaching'

interface Props {
  value: BuildupGrade | null | undefined
  onChange: (grade: BuildupGrade) => void
  disabled?: boolean
  hidePartial?: boolean
}

const OPTIONS: {
  grade: BuildupGrade
  testid: string
  symbol: string
  labelKey: string
  activeClass: string
}[] = [
  {
    grade: 'O',
    testid: 'grade-o',
    symbol: 'O',
    labelKey: 'buildup.grade.correct',
    activeClass: 'bg-lc-mint-900 text-white shadow-[0_2px_0_0_var(--color-lc-mint-dark)]',
  },
  {
    grade: 'TRIANGLE',
    testid: 'grade-triangle',
    symbol: '△',
    labelKey: 'buildup.grade.partial',
    activeClass: 'bg-lc-orange-900 text-white shadow-[0_2px_0_0_var(--color-lc-orange-dark)]',
  },
  {
    grade: 'X',
    testid: 'grade-x',
    symbol: 'X',
    labelKey: 'buildup.grade.incorrect',
    activeClass: 'bg-lc-red-900 text-white shadow-[0_2px_0_0_var(--color-lc-red-dark)]',
  },
]

/**
 * 빌드업 STEP 항목 OXΔ 3-state 채점 토글 (Phase 4a, §5-6=A).
 * 객관식 quiz 의 2-state AnswerOverrideToggle 과 별개 — O(정답)/△(부분정답)/X(오답).
 */
export function BuildupStepGradeToggle({ value, onChange, disabled, hidePartial = false }: Props) {
  const { t } = useTranslation('coaching')
  return (
    <div
      data-testid="buildup-grade-toggle"
      role="group"
      aria-label={t('buildup.grade.ariaLabel')}
      className={clsx(
        'grid w-full max-w-[420px] gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1',
        hidePartial ? 'grid-cols-2' : 'grid-cols-3',
      )}
    >
      {OPTIONS.map((opt) => hidePartial && opt.grade === 'TRIANGLE' ? null : (
        <button
          key={opt.grade}
          type="button"
          data-testid={opt.testid}
          disabled={disabled}
          aria-pressed={value === opt.grade}
          aria-label={t(opt.labelKey)}
          title={t(opt.labelKey)}
          onClick={() => onChange(opt.grade)}
          className={clsx(
            'inline-flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1.5 py-2 text-sm font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1',
            value === opt.grade
              ? opt.activeClass
              : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <span aria-hidden="true" className="text-[15px] font-bold">{opt.symbol}</span>
          <span className="text-center leading-tight">{t(opt.labelKey)}</span>
        </button>
      ))}
    </div>
  )
}
